import { useEffectOnce } from '@dni-react-hooks/use-effect-once'
import { type PopupPosition } from '@hooks-api/types/dataModel/popup'
import { Box } from '@pets-ui/Box/Box'
import { IconButton } from '@pets-ui/IconButton/IconButton'
import { CloseLarge } from '@pets-ui/Icons'
import { Slide } from '@pets-ui/Slide/Slide'
import { Stack } from '@pets-ui/Stack/Stack'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { zIndex } from '@pets-ui/tokens/zIndex.stylex'
import * as stylex from '@stylexjs/stylex'
import { type PopupType } from '../../PopupContainer'
import { PopupContent } from '../PopupContent'
import { PopupHeader } from '../PopupHeader'

const baseStyles = stylex.create({
  root: {
    position: 'fixed',
    zIndex: zIndex.modal,
    bottom: 28,
  },
  left: {
    left: 0,
  },
  right: {
    right: 0,
  },
  closeButton: {
    position: 'absolute',
    height: 32,
    top: -28,
    width: 32,
  },
  closeButtonRight: {
    right: 16,
  },
  closeButtonLeft: {
    left: 16,
  },
  borderRadius: {
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
})

interface PopupFloatingProps {
  open: boolean
  onClose?: () => void
  position?: PopupPosition
  popupKey?: string
  onSubmitPopupImpression?: () => void
  title?: string
  content?: string
  image?: string
  linkUrl?: string
  popupType?: PopupType
  width?: number
  onPopupClickEvent?: () => void
}

export const PopupFloating = (props: PopupFloatingProps) => {
  const {
    onClose,
    open,
    position = 'right',
    popupKey,
    onSubmitPopupImpression,
    title,
    content,
    image,
    linkUrl,
    popupType,
    width,
    onPopupClickEvent,
    ...rest
  } = props

  useEffectOnce(() => {
    if (open && onSubmitPopupImpression) {
      onSubmitPopupImpression()
    }
  })

  return (
    <Slide in={open} direction={position === 'right' ? 'left' : 'right'} rootStyle={[baseStyles.root, baseStyles[position]]}>
      <Box {...rest}>
        <IconButton
          rootStyle={[
            baseStyles.closeButton,
            baseStyles.closeButtonRight,
          ]}
          onClick={onClose}
          data-testid="popup-close-button"
        >
          <Stack p={2} borderRadius="50%" backgroundColor="rgba(0, 0, 0, 0.2)">
            <CloseLarge color={colors.white} />
          </Stack>
        </IconButton>
        <Box pt={4} px={4}>
          {title && <PopupHeader title={title || ''} onClose={onClose} backgroundHidden={true} />}
          <PopupContent
            popupType={popupType}
            content={content || ''}
            image={image || ''}
            linkUrl={linkUrl || ''}
            width={width}
            onClick={onClose}
            onPopupClickEvent={onPopupClickEvent}
          />
        </Box>
      </Box>
    </Slide>
  )
}
